// The "Columns" control a wide table carries: a checkbox per column plus Reset.
//
// It exists because these tables are wide by requirement - the MFD slab table is 17 rows by
// 10 columns, the Leads table carries 13 - and comparing two of those columns means reading
// across the ones in between. Hiding the rest is what makes that possible without exporting
// to a spreadsheet.
//
// Promoted out of MIS_work_2 (`mfd/components/MfdColumnPicker`, now a re-export shim) when
// the Leads and Orders tables needed it too.
//
// Paired with useTableColumns, which owns the state and the persistence; this file is only
// the control surface, so the same rules apply to every table that uses it.

import { Columns3, RotateCcw } from 'lucide-react';

import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import type { TableColumnsApi } from '@/hooks/useTableColumns';

/**
 * The `min-w-*` a wide table should keep once `hiddenCount` of its columns are
 * hidden - roughly the width those columns were taking, given back.
 *
 * Dropping the floor entirely the moment ONE column is hidden (the first cut of
 * this) squeezed the other twelve into the container width, which is the problem
 * the picker exists to solve. Literal class strings, because Tailwind cannot see
 * a computed one, and `undefined` once enough is hidden for the table to fit.
 */
export const tableMinWidthClass = (hiddenCount: number): string | undefined => {
  if (hiddenCount === 0) return 'min-w-[1560px]';
  if (hiddenCount <= 2) return 'min-w-[1320px]';
  if (hiddenCount <= 4) return 'min-w-[1080px]';
  if (hiddenCount <= 6) return 'min-w-[840px]';
  return undefined;
};

export interface ColumnOption<K extends string> {
  key: K;
  label: string;
}

interface ColumnPickerProps<K extends string> {
  columns: readonly ColumnOption<K>[];
  api: TableColumnsApi<K>;
  /** Named in the trigger's accessible label, e.g. "city table". */
  tableLabel: string;
}

export const ColumnPicker = <K extends string>({
  columns,
  api,
  tableLabel,
}: ColumnPickerProps<K>) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        size="sm"
        className="h-8 shrink-0 gap-1.5"
        aria-label={`Choose which columns the ${tableLabel} shows`}
      >
        <Columns3 className="h-3.5 w-3.5" />
        Columns
        {/*
          The count is shown only when something is hidden, and that is the point of it:
          a reader who hid three columns last week and came back to a persisted layout has
          no other clue that the table is not showing everything it has.
        */}
        {api.hiddenCount > 0 ? (
          <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
            {`-${api.hiddenCount}`}
          </span>
        ) : null}
      </Button>
    </PopoverTrigger>

    <PopoverContent align="end" className="w-60 p-2">
      <div className="mb-1 flex items-center justify-between gap-2 px-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Show columns
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 gap-1 px-1.5 text-[11px]"
          onClick={api.reset}
          disabled={api.isDefault}
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </Button>
      </div>

      {/* Capped and scrollable: the slab table has ten columns, which is taller than a
          popover should grow before it starts covering the table it configures. */}
      <div className="max-h-72 space-y-0.5 overflow-y-auto">
        {columns.map((column) => {
          const checked = api.isVisible(column.key);
          // Pinned on by the caller - the columns that say WHICH row this is. Shown
          // checked and disabled rather than dropped from the list, so a reader looking
          // for "Email" finds it here and can see why it cannot be unchecked.
          const locked = api.isLocked(column.key);
          // The last visible column cannot be unchecked - a table with no columns is a
          // blank box whose only way back is the Reset button, which by then is the only
          // thing on screen. Disabled rather than silently ignored, so the rule is legible.
          const isLastVisible = checked && columns.length - api.hiddenCount <= 1;
          const isDisabled = locked || isLastVisible;
          let reason: string | undefined;
          if (locked) reason = 'Always shown';
          else if (isLastVisible) reason = 'At least one column has to stay visible';
          return (
            <label
              key={column.key}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs text-foreground hover:bg-muted/60 aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
              aria-disabled={isDisabled}
              title={reason}
            >
              <Checkbox
                checked={checked}
                disabled={isDisabled}
                onChange={() => api.toggle(column.key)}
              />
              <span className="truncate">{column.label}</span>
            </label>
          );
        })}
      </div>
    </PopoverContent>
  </Popover>
);
